/**
 * Client side filtering of tables and of the FAQ accordions.
 */

import { foldAccents, normalisedText, query, queryAll } from "../core/dom";

/** Return whether `haystack` contains every word of `needle`. */
export function matches(haystack: string, needle: string): boolean {
    const folded = foldAccents(haystack);
    const words = foldAccents(needle).split(/\s+/).filter((word) => word.length > 0);

    return words.every((word) => folded.includes(word));
}

/** Filter the rows of `table` and return how many stayed visible. */
export function filterTable(table: HTMLTableElement, term: string): number {
    const rows = queryAll<HTMLTableRowElement>("tbody tr", table);
    let visible = 0;

    for (const row of rows) {
        const isMatch = term.length === 0 || matches(normalisedText(row), term);
        row.hidden = !isMatch;

        if (isMatch) {
            visible += 1;
        }
    }

    return visible;
}

/** Filter arbitrary elements carrying a `data-filter-text` attribute. */
export function filterElements(elements: readonly HTMLElement[], term: string): number {
    let visible = 0;

    for (const element of elements) {
        const haystack = element.dataset["filterText"] ?? normalisedText(element);
        const isMatch = term.length === 0 || matches(haystack, term);
        element.hidden = !isMatch;

        if (isMatch) {
            visible += 1;
        }
    }

    return visible;
}

/**
 * Return what a search matching nothing should put away.
 *
 * The panel around the table, so that a table of headings above an empty
 * space does not read as a broken page - unless the search box is inside
 * that very panel, in which case putting it away would take the box the
 * visitor is typing in with it. Then only the table itself goes.
 */
export function hideableFor(table: HTMLTableElement, input: HTMLElement): HTMLElement {
    const panel = table.closest<HTMLElement>(".panel");

    if (panel && !panel.contains(input)) {
        return panel;
    }

    return table.closest<HTMLElement>(".table-wrap") ?? table;
}

/**
 * Wire the inputs filtering a table referenced by `data-table-filter`.
 *
 * A search which matches nothing leaves a table of headings above an empty
 * space, which reads as a broken page rather than as an answer. It is put
 * away instead and the message beside it takes its place.
 */
export function initTableFilters(): void {
    for (const input of queryAll<HTMLInputElement>("[data-table-filter]")) {
        const selector = input.dataset["tableFilter"] ?? "";
        const table = query<HTMLTableElement>(selector);

        if (!table) {
            continue;
        }

        const panel = hideableFor(table, input);
        const nothing = query<HTMLElement>("[data-filter-empty]");

        input.addEventListener("input", () => {
            const visible = filterTable(table, input.value.trim());

            panel.hidden = visible === 0;

            if (nothing) {
                nothing.hidden = visible > 0;
            }
        });
    }
}

/**
 * Wire the inputs filtering elements referenced by `data-filter-target`.
 *
 * A search which matches nothing says so, in the same words and through the
 * same `[data-filter-empty]` block the tables use. It used to look the answer
 * up by an id belonging to one page of the portal, so every other list which
 * filtered simply went quiet.
 */
export function initElementFilters(): void {
    for (const input of queryAll<HTMLInputElement>("[data-filter-target]")) {
        const selector = input.dataset["filterTarget"] ?? "";
        const targets = queryAll<HTMLElement>(selector);

        if (targets.length === 0) {
            continue;
        }

        const empty = query<HTMLElement>("[data-filter-empty]");

        input.addEventListener("input", () => {
            const visible = filterElements(targets, input.value.trim());

            if (empty) {
                empty.hidden = visible > 0;
            }
        });
    }
}
